import { AlertTriangle } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  apiErrorMessage,
  comparisonRows,
  placeholderDescription,
  placeholderTitle,
  type ComparisonRow,
} from '@/pages/DuplicateReviewPage.helpers'
import type {
  DuplicateSuggestion,
  DuplicateSuggestionContact,
  SelectedDuplicatePair,
  SuggestionListState,
} from '@/pages/DuplicateReviewPage.types'

const contactBadgeVariant: Record<
  DuplicateSuggestionContact['contact_type'],
  BadgeProps['variant']
> = {
  Person: 'person',
  Organization: 'organization',
}

export function SuggestionList({
  suggestions,
  selectedSuggestionId,
  state,
  onSelect,
}: {
  suggestions: DuplicateSuggestion[]
  selectedSuggestionId: number | null
  state: SuggestionListState
  onSelect: (suggestion: DuplicateSuggestion) => void
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-body font-normal text-foreground">Open pairs</h2>
      </div>
      <SuggestionListBody
        suggestions={suggestions}
        selectedSuggestionId={selectedSuggestionId}
        state={state}
        onSelect={onSelect}
      />
    </Card>
  )
}

function SuggestionListBody({
  suggestions,
  selectedSuggestionId,
  state,
  onSelect,
}: {
  suggestions: DuplicateSuggestion[]
  selectedSuggestionId: number | null
  state: SuggestionListState
  onSelect: (suggestion: DuplicateSuggestion) => void
}) {
  if (state === 'loading') {
    return <EmptyState title="Loading Duplicate Suggestions" className="min-h-52" />
  }

  if (state === 'error') {
    return (
      <EmptyState
        title="Duplicate Suggestions unavailable"
        description="Refresh and try again."
        className="min-h-52"
      />
    )
  }

  if (suggestions.length === 0) {
    return (
      <EmptyState
        title="No Duplicate Suggestions"
        description="The review queue is clear."
        className="min-h-52"
      />
    )
  }

  return (
    <div className="divide-y divide-border">
      {suggestions.map((suggestion) => (
        <SuggestionListRow
          key={suggestion.id}
          suggestion={suggestion}
          active={suggestion.id === selectedSuggestionId}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

function SuggestionListRow({
  suggestion,
  active,
  onSelect,
}: {
  suggestion: DuplicateSuggestion
  active: boolean
  onSelect: (suggestion: DuplicateSuggestion) => void
}) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-current={active ? 'true' : undefined}
      className={cn(
        'h-auto w-full justify-start rounded-none border-0 bg-card px-4 py-3 text-left hover:bg-muted',
        active && 'bg-accent text-accent-foreground hover:bg-accent',
      )}
      onClick={() => onSelect(suggestion)}
    >
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-body-sm">
          {suggestion.contact_one.name} and {suggestion.contact_two.name}
        </span>
        <span className="text-caption font-normal text-muted-foreground">
          {suggestion.contact_one.contact_type}
        </span>
      </span>
    </Button>
  )
}

export function ComparisonCard({
  suggestion,
  survivorId,
  onSurvivorChange,
}: {
  suggestion: DuplicateSuggestion
  survivorId: number
  onSurvivorChange: (contactId: number) => void
}) {
  const left = suggestion.contact_one
  const right = suggestion.contact_two

  return (
    <Card className="overflow-x-auto">
      <div className="grid min-w-[640px] grid-cols-[150px_1fr_1fr] border-b border-border bg-muted/40">
        <div className="p-4" />
        <ContactHeader contact={left} />
        <ContactHeader contact={right} />
      </div>
      {comparisonRows(left, right).map((row) => (
        <ComparisonRowView
          key={row.field}
          row={row}
          left={left}
          right={right}
          survivorId={survivorId}
          onSurvivorChange={onSurvivorChange}
        />
      ))}
    </Card>
  )
}

function ContactHeader({ contact }: { contact: DuplicateSuggestionContact }) {
  return (
    <div className="border-l border-border p-4">
      <div className="flex items-center gap-2">
        <span className="min-w-0 truncate text-body text-foreground">{contact.name}</span>
        <Badge variant={contactBadgeVariant[contact.contact_type]}>{contact.contact_type}</Badge>
      </div>
      <p className="pt-0.5 text-caption text-muted-foreground">{contact.email ?? '-'}</p>
    </div>
  )
}

function ComparisonRowView({
  row,
  left,
  right,
  survivorId,
  onSurvivorChange,
}: {
  row: ComparisonRow
  left: DuplicateSuggestionContact
  right: DuplicateSuggestionContact
  survivorId: number
  onSurvivorChange: (contactId: number) => void
}) {
  return (
    <div className="grid min-w-[640px] grid-cols-[150px_1fr_1fr] border-b border-border last:border-0">
      <div className="p-4 text-caption uppercase tracking-wide text-muted-foreground">
        {row.label}
      </div>
      <ComparisonCell
        contact={left}
        fieldLabel={row.label}
        value={row.left}
        conflict={row.conflict}
        selected={survivorId === left.id}
        onKeep={() => onSurvivorChange(left.id)}
      />
      <ComparisonCell
        contact={right}
        fieldLabel={row.label}
        value={row.right}
        conflict={row.conflict}
        selected={survivorId === right.id}
        onKeep={() => onSurvivorChange(right.id)}
      />
    </div>
  )
}

function ComparisonCell({
  contact,
  fieldLabel,
  value,
  conflict,
  selected,
  onKeep,
}: {
  contact: DuplicateSuggestionContact
  fieldLabel: string
  value: string
  conflict: boolean
  selected: boolean
  onKeep: () => void
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 items-center justify-between gap-3 border-l border-border p-4 text-body-sm',
        conflict ? 'bg-warning/10 text-foreground' : 'text-muted-foreground',
      )}
    >
      <span className="min-w-0 truncate">{value}</span>
      {conflict && (
        <Button
          type="button"
          size="sm"
          variant={selected ? 'default' : 'outline'}
          aria-label={`Keep ${contact.name} for ${fieldLabel}`}
          aria-pressed={selected}
          onClick={onKeep}
        >
          Keep
        </Button>
      )}
    </div>
  )
}

export function MergeDialog({
  open,
  pair,
  confirmation,
  error,
  pending,
  onConfirmationChange,
  onMerge,
  onOpenChange,
}: {
  open: boolean
  pair: SelectedDuplicatePair
  confirmation: string
  error: Error | null
  pending: boolean
  onConfirmationChange: (value: string) => void
  onMerge: () => void
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Merge {pair.merged.name} into {pair.survivor.name}?
          </DialogTitle>
          <DialogDescription>
            Merge is irreversible. The survivor keeps conflicts, blanks fill from the merged-away
            Contact, and history is retained.
          </DialogDescription>
        </DialogHeader>
        <MergeDialogBody
          pair={pair}
          confirmation={confirmation}
          error={error}
          onConfirmationChange={onConfirmationChange}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={confirmation !== pair.merged.name || pending}
            onClick={onMerge}
          >
            Merge contacts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MergeDialogBody({
  pair,
  confirmation,
  error,
  onConfirmationChange,
}: {
  pair: SelectedDuplicatePair
  confirmation: string
  error: Error | null
  onConfirmationChange: (value: string) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm text-foreground">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" strokeWidth={1.5} />
        <p>
          {pair.merged.name} becomes an archived tombstone pointing to {pair.survivor.name}.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="merge-confirm">Type {pair.merged.name} to confirm</Label>
        <Input
          id="merge-confirm"
          value={confirmation}
          placeholder={pair.merged.name}
          onChange={(event) => onConfirmationChange(event.target.value)}
        />
      </div>
      {error && (
        <p role="alert" className="text-body-sm text-destructive">
          {apiErrorMessage(error)}
        </p>
      )}
    </div>
  )
}

export function ReviewPlaceholder({ state }: { state: SuggestionListState }) {
  return (
    <Card>
      <EmptyState
        title={placeholderTitle(state)}
        description={placeholderDescription(state)}
        className="min-h-72"
      />
    </Card>
  )
}
